import { useEffect, useState } from 'react';
import { AdminLayout } from '@/admin/components/AdminLayout';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface CalculatorDefaults {
  monthlyRate: number;
  stampTaxRate: number;
  stampTaxCap: number;
  ivaRate: number;
}

interface InstallmentRow {
  amount: string;
  advanceDays: string;
}

interface SimulateInstallment {
  index: number;
  amount: number;
  advanceDays: number;
  discountedValue: number;
  commissionAmount: number;
  commissionRatio: number;
}

interface SimulateResult {
  config: CalculatorDefaults;
  dailyRate: number;
  installments: SimulateInstallment[];
  totalAmount: number;
  totalCommission: number;
  stampTaxAmount: number;
  netAmount: number;
}

/**
 * Regla de negocio (R01): la primera cuota de una venta NO se adelanta — el adquirente la abona
 * en ~24h. El backend la excluye del listado de cuotas adelantables (advance-requests.service.ts).
 * Por eso la calculadora solo recibe cuotas adelantables (días > 0): incluir la cuota 1 inflaba
 * `N` en la fórmula TYE (`baseTye × N × tasaTYE`) y producía un total distinto al de /adelantos/cuotas.
 */
const PRESETS: { label: string; description: string; rows: InstallmentRow[] }[] = [
  {
    label: 'Excel — 3 × $80.000',
    description: 'Venta a 3 cuotas: adelanta cuotas 2 y 3 (días [30, 60])',
    rows: [
      { amount: '78400', advanceDays: '30' },
      { amount: '78400', advanceDays: '60' },
    ],
  },
  {
    label: '6 cuotas $50.000',
    description: 'Venta a 6 cuotas: adelanta cuotas 2..6 (días [30..150])',
    rows: Array.from({ length: 5 }, (_, i) => ({ amount: '50000', advanceDays: String((i + 1) * 30) })),
  },
  {
    label: '12 cuotas $100.000',
    description: 'Venta a 12 cuotas: adelanta cuotas 2..12, anual sin tope TYE',
    rows: Array.from({ length: 11 }, (_, i) => ({ amount: '100000', advanceDays: String((i + 1) * 30) })),
  },
  {
    label: '13 cuotas $100.000',
    description: 'Venta a 13 cuotas: adelanta cuotas 2..13, tope TYE 0,8% activo',
    rows: Array.from({ length: 12 }, (_, i) => ({ amount: '100000', advanceDays: String((i + 1) * 30) })),
  },
];

const fmtCLP = (v: number) => new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(Math.round(v));
const fmtPct = (v: number, digits = 4) => `${(v * 100).toFixed(digits)}%`;

export default function Calculadora() {
  const [defaults, setDefaults] = useState<CalculatorDefaults | null>(null);
  const [config, setConfig] = useState<CalculatorDefaults>({ monthlyRate: 0.0099, stampTaxRate: 0.00066, stampTaxCap: 0.008, ivaRate: 0.19 });
  const [rows, setRows] = useState<InstallmentRow[]>([
    { amount: '100000', advanceDays: '30' },
    { amount: '100000', advanceDays: '60' },
    { amount: '100000', advanceDays: '90' },
  ]);
  const [result, setResult] = useState<SimulateResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [calcGeneration, setCalcGeneration] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const d = await adminHttpClient.get<CalculatorDefaults>(getAdminEndpointUrl('calculator', 'defaults'));
        setDefaults(d);
        setConfig(d);
      } catch (err: any) {
        setError(err?.message ?? 'No se pudieron cargar las tarifas por defecto');
      } finally {
        setLoadingDefaults(false);
      }
    })();
  }, []);

  const dailyRate = Math.pow(1 + config.monthlyRate, 1 / 30) - 1;

  const updateRow = (i: number, key: keyof InstallmentRow, value: string) => {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  };
  const addRow = () => setRows((prev) => [...prev, { amount: '100000', advanceDays: '30' }]);
  const removeRow = (i: number) => {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  };
  const applyPreset = (rows: InstallmentRow[]) => {
    setRows(rows);
    setResult(null);
  };
  const resetConfig = () => {
    if (defaults) setConfig(defaults);
  };

  const handleCalculate = async () => {
    setError(null);
    setLoading(true);
    try {
      const installments = rows
        .map((r) => ({ amount: Number(r.amount), advanceDays: Number(r.advanceDays) }))
        .filter((r) => Number.isFinite(r.amount) && r.amount > 0 && Number.isFinite(r.advanceDays) && r.advanceDays >= 0);
      if (installments.length === 0) throw new Error('Agrega al menos una cuota válida.');
      const data = await adminHttpClient.post<SimulateResult>(getAdminEndpointUrl('calculator', 'simulate'), {
        installments,
        config,
      });
      setResult(data);
      setCalcGeneration((g) => g + 1);
    } catch (err: any) {
      setError(err?.data?.message ?? err?.message ?? 'No se pudo calcular');
    } finally {
      setLoading(false);
    }
  };

  const overrideActive = !!defaults && (
    Math.abs(defaults.monthlyRate - config.monthlyRate) > 1e-12 ||
    Math.abs(defaults.stampTaxRate - config.stampTaxRate) > 1e-12 ||
    Math.abs(defaults.stampTaxCap - config.stampTaxCap) > 1e-12 ||
    Math.abs(defaults.ivaRate - config.ivaRate) > 1e-12
  );

  return (
    <AdminLayout
      title="Calculadora de adelantos"
      subtitle="Simula comisión, TYE y neto sin persistir nada — usa el mismo algoritmo del flujo productivo"
      actions={
        <Button
          size="sm"
          className="gap-1.5 bg-blue-600 hover:bg-blue-500 h-9"
          disabled={loading || rows.length === 0}
          onClick={handleCalculate}
        >
          <span className="material-symbols-outlined text-[16px]">{loading ? 'progress_activity' : 'play_arrow'}</span>
          {loading ? 'Calculando...' : 'Calcular'}
        </Button>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
        {/* ── Parámetros ───────────────────────────────────────── */}
        <section className="xl:col-span-7 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <header className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800/80">
            <div className="flex items-center gap-2.5">
              <span className="material-symbols-outlined text-[18px] text-blue-500">tune</span>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 tracking-tight">Parámetros</h3>
              {overrideActive && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-full px-2 py-0.5">
                  Modificados
                </span>
              )}
            </div>
            <button
              onClick={resetConfig}
              disabled={!overrideActive || loadingDefaults}
              className="text-[11px] font-medium text-slate-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
            >
              <span className="material-symbols-outlined text-[14px]">restart_alt</span>
              Restaurar defaults
            </button>
          </header>
          <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <ConfigField label="Tasa mensual" value={config.monthlyRate} onChange={(v) => setConfig({ ...config, monthlyRate: v })} hint={`${(config.monthlyRate * 100).toFixed(2)}%`} step={0.0001} />
            <ConfigField label="TYE — tasa" value={config.stampTaxRate} onChange={(v) => setConfig({ ...config, stampTaxRate: v })} hint={`${(config.stampTaxRate * 100).toFixed(3)}% / mes`} step={0.00001} />
            <ConfigField label="TYE — tope" value={config.stampTaxCap} onChange={(v) => setConfig({ ...config, stampTaxCap: v })} hint={`${(config.stampTaxCap * 100).toFixed(2)}% máx.`} step={0.0001} />
            <ConfigField label="IVA" value={config.ivaRate} onChange={(v) => setConfig({ ...config, ivaRate: v })} hint={`${(config.ivaRate * 100).toFixed(0)}%`} step={0.01} />
          </div>
          <div className="px-5 pb-5">
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg px-4 py-3 flex items-baseline justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Tasa diaria efectiva</p>
                <p className="text-[10px] text-slate-400 font-mono mt-0.5">(1 + r)^(1/30) − 1</p>
              </div>
              <span className="text-base font-mono font-semibold text-slate-800 dark:text-slate-200 tabular-nums">{fmtPct(dailyRate, 6)}</span>
            </div>
          </div>
        </section>

        {/* ── Hero de resultado / TOTALES ─────────────────────── */}
        <section className="xl:col-span-5">
          <div className="relative overflow-hidden rounded-xl border border-slate-900/90 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 text-white shadow-xl shadow-slate-900/10">
            <div className="absolute inset-0 opacity-[0.07] bg-[radial-gradient(ellipse_at_top_right,_white,_transparent_60%)]" />
            <div className="relative p-5 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-300/80">Resultado</p>
                <span className="text-[10px] text-slate-400 font-mono">
                  {result ? `${result.installments.length} cuota${result.installments.length === 1 ? '' : 's'}` : '—'}
                </span>
              </div>

              <div key={`net-${calcGeneration}`} className="animate-[fadeIn_400ms_ease-out]">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Neto a abonar</p>
                <p className="text-[40px] sm:text-[44px] leading-none font-bold tabular-nums tracking-tight">
                  {result ? `$${fmtCLP(result.netAmount)}` : <span className="text-slate-600">$ —</span>}
                </p>
                {result && (
                  <p className="text-[11px] text-slate-400 mt-1.5 font-mono">
                    {fmtCLP(result.totalAmount)} − {fmtCLP(result.totalCommission)} − {fmtCLP(result.stampTaxAmount)}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-800">
                <Stat label="Bruto" value={result ? fmtCLP(result.totalAmount) : '—'} />
                <Stat label="Comisión" value={result ? fmtCLP(result.totalCommission) : '—'} sub={result && result.totalAmount > 0 ? `${((result.totalCommission / result.totalAmount) * 100).toFixed(2)}%` : undefined} accent="blue" />
                <Stat label="TYE" value={result ? fmtCLP(result.stampTaxAmount) : '—'} sub={result && result.totalAmount > 0 ? `${((result.stampTaxAmount / result.totalAmount) * 100).toFixed(3)}%` : undefined} accent="amber" />
              </div>
            </div>
          </div>
        </section>

        {/* ── Cuotas a simular ────────────────────────────────── */}
        <section className="xl:col-span-12 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
          <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800/80">
            <div className="flex items-center gap-2.5">
              <span className="material-symbols-outlined text-[18px] text-blue-500">view_list</span>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 tracking-tight">Cuotas a adelantar</h3>
              <span className="text-[10px] font-mono text-slate-400">({rows.length})</span>
              <span
                className="text-[10px] font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5"
                title="R01: la cuota 1 la abona el adquirente en ~24h y nunca se adelanta. Empieza desde la cuota 2."
              >
                excluye cuota 1
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  onClick={() => applyPreset(p.rows)}
                  title={p.description}
                  className="text-[11px] font-medium px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 hover:border-blue-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={addRow}
                className="text-[11px] font-medium px-2.5 py-1.5 rounded-md border border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center gap-1"
              >
                <span className="material-symbols-outlined text-[14px]">add</span>
                Cuota
              </button>
            </div>
          </header>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50/60 dark:bg-slate-950/40 border-b border-slate-100 dark:border-slate-800/80">
                  <th className="text-left px-5 py-2.5 font-semibold w-[60px]">#</th>
                  <th className="text-left px-5 py-2.5 font-semibold">Monto cuota (CLP)</th>
                  <th className="text-left px-5 py-2.5 font-semibold w-[180px]">Días anticipación</th>
                  <th className="text-right px-5 py-2.5 font-semibold w-[160px]">Descontado</th>
                  <th className="text-right px-5 py-2.5 font-semibold w-[160px]">Comisión</th>
                  <th className="text-right px-5 py-2.5 font-semibold w-[100px]">% comisión</th>
                  <th className="px-5 py-2.5 w-[60px]"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const r = result?.installments[i];
                  const isUnadvanced = Number(row.advanceDays) === 0;
                  return (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-800/60 last:border-b-0 hover:bg-slate-50/40 dark:hover:bg-slate-800/20 transition-colors">
                      <td className="px-5 py-2 text-slate-400 font-mono text-[11px] tabular-nums">{String(i + 2).padStart(2, '0')}</td>
                      <td className="px-5 py-2">
                        <Input
                          type="number"
                          value={row.amount}
                          onChange={(e) => updateRow(i, 'amount', e.target.value)}
                          className="h-8 text-sm font-mono tabular-nums w-full max-w-[180px]"
                          min={0}
                          step={1}
                        />
                      </td>
                      <td className="px-5 py-2">
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={row.advanceDays}
                            onChange={(e) => updateRow(i, 'advanceDays', e.target.value)}
                            className="h-8 text-sm font-mono tabular-nums w-[100px]"
                            min={0}
                            step={1}
                          />
                          {isUnadvanced ? (
                            <span className="text-[10px] font-medium text-slate-500 bg-slate-100 dark:bg-slate-800 rounded-full px-2 py-0.5">no se adelanta</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-5 py-2 text-right font-mono tabular-nums text-[13px] text-slate-500 dark:text-slate-400">
                        {r ? fmtCLP(r.discountedValue) : '—'}
                      </td>
                      <td className="px-5 py-2 text-right font-mono tabular-nums text-[13px] font-semibold text-slate-800 dark:text-slate-200">
                        {r ? (r.commissionAmount > 0 ? fmtCLP(r.commissionAmount) : '0') : '—'}
                      </td>
                      <td className="px-5 py-2 text-right font-mono tabular-nums text-[12px] text-slate-500">
                        {r ? `${(r.commissionRatio * 100).toFixed(2)}%` : '—'}
                      </td>
                      <td className="px-5 py-2 text-right">
                        <button
                          onClick={() => removeRow(i)}
                          disabled={rows.length === 1}
                          className="text-slate-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          aria-label={`Eliminar cuota ${i + 1}`}
                        >
                          <span className="material-symbols-outlined text-[18px]">delete</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {result && (
                <tfoot>
                  <tr className="bg-slate-50 dark:bg-slate-950/40 text-[12px]">
                    <td className="px-5 py-3 font-semibold text-slate-500 uppercase tracking-wider text-[10px]" colSpan={3}>Total</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-500">—</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums font-bold text-slate-900 dark:text-slate-100">{fmtCLP(result.totalCommission)}</td>
                    <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-500">
                      {result.totalAmount > 0 ? `${((result.totalCommission / result.totalAmount) * 100).toFixed(2)}%` : '—'}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>

        {/* ── Notas / fórmula ─────────────────────────────────── */}
        <section className="xl:col-span-12 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm p-5">
          <div className="flex items-start gap-3">
            <span className="material-symbols-outlined text-[18px] text-blue-500 mt-0.5">function</span>
            <div className="flex-1 space-y-1.5">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">Algoritmo</h3>
              <ul className="text-[12px] text-slate-600 dark:text-slate-400 leading-relaxed font-mono space-y-0.5">
                <li>tasaDiaria = (1 + tasaMensual)^(1/30) − 1</li>
                <li>descontado<sub>i</sub> = round(monto<sub>i</sub> / (1 + tasaDiaria)^días<sub>i</sub>)</li>
                <li>comisión<sub>i</sub> = round((monto<sub>i</sub> − descontado<sub>i</sub>) × (1 + IVA))</li>
                <li>baseTYE = Σ monto<sub>i</sub> donde días<sub>i</sub> &gt; 0</li>
                <li>TYE = round(MIN(baseTYE × N × tasaTYE, baseTYE × tope)) — N = nº de cuotas adelantadas</li>
                <li>neto = total − comisión − TYE</li>
              </ul>
              <p className="text-[11px] text-slate-500 pt-2">
                La cuota 1 de la venta no entra al cálculo (R01 — la abona el adquirente en ~24h). El backend ya la filtra del listado de <code className="bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 font-mono">/adelantos/cuotas</code>; esta calculadora replica el mismo input.
              </p>
              <p className="text-[11px] text-slate-500">
                Single source of truth: <code className="bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 font-mono">src/utils/calculate-advance-commission.ts</code> en el API. Esta calculadora reusa exactamente ese código vía <code className="bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 font-mono">/admin/calculator/simulate</code>.
              </p>
            </div>
          </div>
        </section>
      </div>

      {error && (
        <div className="fixed bottom-6 right-6 max-w-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 shadow-lg flex items-start gap-2 text-xs text-red-700 dark:text-red-400 animate-[fadeIn_200ms_ease-out] z-50">
          <span className="material-symbols-outlined text-[18px] flex-shrink-0">error</span>
          <div className="flex-1">
            <p className="font-semibold mb-0.5">No se pudo calcular</p>
            <p>{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <span className="material-symbols-outlined text-[16px]">close</span>
          </button>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </AdminLayout>
  );
}

function ConfigField({ label, value, onChange, hint, step }: { label: string; value: number; onChange: (v: number) => void; hint: string; step: number }) {
  return (
    <div>
      <label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
      <Input
        type="number"
        value={value}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          if (Number.isFinite(n)) onChange(n);
        }}
        step={step}
        className="h-9 text-sm font-mono tabular-nums"
      />
      <p className="text-[10px] text-slate-400 mt-1 font-mono">{hint}</p>
    </div>
  );
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: 'blue' | 'amber' }) {
  const valueClass = accent === 'blue' ? 'text-blue-300' : accent === 'amber' ? 'text-amber-300' : 'text-slate-100';
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">{label}</p>
      <p className={`text-base font-bold tabular-nums ${valueClass}`}>{value === '—' ? value : `$${value}`}</p>
      {sub && <p className="text-[10px] text-slate-500 font-mono mt-0.5">{sub}</p>}
    </div>
  );
}
